import React, { useMemo } from 'react'
import { faker } from '@faker-js/faker'

import { getCustomers } from './faker/customer'
import {
  getInvoices,
  type Invoice,
  validateInvoices,
} from './faker/invoice'
import { randomIntegerInRange } from './faker/utils'
import { useLayerApiUrl } from './ImportDataContext'
import {
  type BusinessIndustry,
  createCustomTransactions,
  createMatchingCustomTransactionsFromInvoices,
  type CustomAccount,
  ImportApiError,
} from './importDummyDataHelpers'
import { ImportForm } from './ImportForm'
import { type ImportStepDef } from './useImportForm'

export interface ImportInvoicesProps {
  demoName: string
  businessId: string
  industry: BusinessIndustry
  businessAccessToken: string
  defaultCustomAccount: CustomAccount
}

export type InvoiceWithCustomer = Invoice & { customer_external_id: string }

const buildInvoiceSteps = (
  demoName: string,
  businessId: string,
  businessAccessToken: string,
  customAccountId: string,
  layerApiUrl: string,
) => (invoices: Invoice[]): ImportStepDef[] => {
  const customers = getCustomers(10, demoName)

  let invoicesWithCustomers: InvoiceWithCustomer[] = []
  return [
    {
      label: 'Creating customers',
      run: async () => {
        const customerResponse = await fetch(
          `${layerApiUrl}/v1/businesses/${businessId}/customers/bulk`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${businessAccessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(customers),
          },
        )
        if (!customerResponse.ok) {
          throw new ImportApiError(
            `Customer creation API call failed: ${customerResponse.status} ${customerResponse.statusText}`,
            customerResponse.status,
          )
        }
      },
    },
    {
      label: 'Creating invoices',
      run: async () => {
        invoicesWithCustomers = invoices.map((invoice: Invoice) => ({
          ...invoice,
          customer_external_id:
            customers[randomIntegerInRange(0, 9)].external_id,
        } as InvoiceWithCustomer))

        const invoiceResponse = await fetch(
          `${layerApiUrl}/v1/businesses/${businessId}/invoices/bulk`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${businessAccessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(invoicesWithCustomers),
          },
        )
        if (!invoiceResponse.ok) {
          throw new ImportApiError(
            `Invoice API call failed: ${invoiceResponse.status} ${invoiceResponse.statusText}`,
            invoiceResponse.status,
          )
        }
      },
    },
    {
      label: 'Creating matching transactions',
      run: async () => {
        const matchingTxns = createMatchingCustomTransactionsFromInvoices(
          invoicesWithCustomers,
          demoName,
        )
        await createCustomTransactions(
          businessId,
          businessAccessToken,
          customAccountId,
          matchingTxns,
          layerApiUrl,
        )
      },
    },
  ]
}

export const ImportInvoices = ({
  demoName,
  businessId,
  businessAccessToken,
  defaultCustomAccount,
}: ImportInvoicesProps) => {
  const layerApiUrl = useLayerApiUrl()

  const getSteps = useMemo(
    () => buildInvoiceSteps(
      demoName,
      businessId,
      businessAccessToken,
      defaultCustomAccount.id,
      layerApiUrl,
    ),
    [demoName, businessId, businessAccessToken, defaultCustomAccount.id, layerApiUrl],
  )

  return (
    <ImportForm
      title='Invoices'
      defaultCustomAccount={defaultCustomAccount}
      getInitialData={() => {
        faker.seed(42)
        return getInvoices(demoName)
      }}
      validateData={validateInvoices}
      getSteps={getSteps}
      dependencies={[demoName]}
    />
  )
}
